import { useState, useCallback, useEffect, useRef } from "react";
import { fetchUserProfiles, fetchUserNotificationEligibility } from "./api";

// Default fetcher: orchestrates the two PostgREST endpoints
// (admin_profiles_lookup + admin_user_notification_eligibility) and
// merges them into the candidate shape the hook produces. Tests can
// inject a custom fetchDetails to bypass network entirely.
async function defaultFetchDetails(ids, { notificationDbName }) {
  const [profiles, eligibilities] = await Promise.all([
    fetchUserProfiles(ids),
    notificationDbName ? fetchUserNotificationEligibility(ids, notificationDbName) : Promise.resolve([]),
  ]);
  const eligibilityById = new Map(eligibilities.map((row) => [String(row.user_id), row]));
  return profiles.map((profile) => {
    const elig = eligibilityById.get(String(profile.id));
    return {
      ...profile,
      is_eligible: elig?.is_eligible ?? null,
      ineligibility_reasons: elig?.reasons || [],
    };
  });
}

// Manages the accumulated pool of candidate recipients across multiple
// searches. Each candidate carries the source group that contributed
// them ({type, params}) so Stage 2 can render grouped collapsible
// sections.
//
// Dedup-at-add: when a search produces a candidate already in the pool,
// the second add is a no-op (first source wins). Surface count of
// duplicates separately so the picker can display "12 already in pool".
//
// Hydration priorities (in order):
//   1. initialCandidates — full display details already in memory on
//      the parent form. The normal in-session case: admin opens the
//      picker → adds rows → modal closes → reopens picker. The parent's
//      recipientDisplayPool already holds everything we need; reusing
//      it avoids a PostgREST round-trip AND prevents the bug where a
//      slow / failed / RLS-quirked lookup turns all known-good
//      recipients into "(deleted user)" stubs on the second open.
//   2. fetchDetails(missingIds) — fallback for ids in initialIds that
//      aren't covered by initialCandidates. The bare edit-mode case
//      (server-rendered form for an existing story idea, no client-
//      side candidate cache yet) lands here.
//   3. Stub row — last resort for ids that neither source resolved.
//
// initialGroups is used to assign source provenance to ids that have
// to fall back to fetch / stub, so the grouped view in the form keeps
// its original shape.
//
// Returns canonical formik-shaped data via toFormData(): { ids: uuid[],
// groups: [{source_type, source_params, user_ids}] }. The model
// invariant (selected_recipient_groups_match_ids) requires exact
// agreement between the two arrays — we maintain that here.
export default function useCandidatePool({
  initialIds = [],
  initialGroups = [],
  initialCandidates = [],
  // The notification context drives the eligibility decoration on
  // each candidate. Required for #3121 picker use (
  // 'media_new_story_idea'); future pickers for other notifications
  // pass their own db_name. Falsy → eligibility is left null on
  // candidates (no decoration), which is the right default for
  // generic profile-display callers.
  notificationDbName = null,
  fetchDetails = defaultFetchDetails,
  onUserChange,
} = {}) {
  const [pool, setPool] = useState([]);
  const [hydrating, setHydrating] = useState(initialIds.length > 0);

  const onUserChangeRef = useRef(onUserChange);
  useEffect(() => {
    onUserChangeRef.current = onUserChange;
  });
  const notifyUserChange = useCallback(() => {
    onUserChangeRef.current?.();
  }, []);

  // Reads initialIds / initialGroups / initialCandidates via ref so
  // this effect doesn't refire if the parent passes fresh array
  // identities each render.
  const initialIdsRef = useRef(initialIds);
  const initialGroupsRef = useRef(initialGroups);
  const initialCandidatesRef = useRef(initialCandidates);
  useEffect(() => {
    const ids = initialIdsRef.current;
    if (!ids || ids.length === 0) {
      setHydrating(false);
      return undefined;
    }

    // Index initialCandidates so we can look up by id. Walk these
    // first: known-good display details from the parent override any
    // fetched/stub data.
    const candidatesById = new Map((initialCandidatesRef.current || []).map((c) => [String(c.id), c]));

    // Capture source for each id from the stored groups (used as
    // fallback when an id isn't covered by initialCandidates).
    const sourceById = new Map();
    (initialGroupsRef.current || []).forEach((group) => {
      const source = {
        type: group.source_type || "unknown",
        params: group.source_params || {},
      };
      (group.user_ids || []).forEach((id) => {
        sourceById.set(String(id), source);
      });
    });

    // Build pool order from groups (preserves the admin's curated
    // ordering across reopens), with any stray ids appended at the end.
    const orderedIds = [];
    const seen = new Set();
    (initialGroupsRef.current || []).forEach((group) => {
      (group.user_ids || []).forEach((id) => {
        const idStr = String(id);
        if (seen.has(idStr)) return;
        seen.add(idStr);
        orderedIds.push(idStr);
      });
    });
    ids.forEach((id) => {
      const idStr = String(id);
      if (seen.has(idStr)) return;
      seen.add(idStr);
      orderedIds.push(idStr);
    });

    // JS-layer stub-row synthesis: admin_recipients_lookup returns rows
    // only for user_ids that still exist; for missing UUIDs we fall back
    // to a synthesised "(deleted user)" stub built inline in the .then
    // block below (with is_stub: true so the UI can render it
    // distinctively). This pattern keeps the hydration logic colocated
    // with the picker that consumes it.
    //
    // Consider extracting this synthesis to a server-side PostgREST RPC
    // (so the endpoint always returns a complete row shape and JS becomes
    // a thin consumer) if any of these conditions fires:
    //
    //   1. A second consumer of the same stub-row pattern arrives (e.g.
    //      another admin tool needing recipient hydration with missing-row
    //      tolerance). Two JS-side reimplementations of the synthesis
    //      logic is the prompt to extract.
    //   2. The JS-side stub-construction below grows past ~10 lines (more
    //      fields per row, or more conditional logic). The cost of keeping
    //      it in JS grows non-linearly with the field-shape.
    //   3. A new column is added to admin_recipients_lookup that's
    //      *expensive to compute for non-existent rows* (e.g. a COALESCE
    //      against a derived column) — at that point the cost of asking
    //      the view to return stubs server-side is lower than the cost of
    //      synthesising them client-side.
    //
    // Until then, the JS-layer approach is the right shape — small diff,
    // colocated with the consumer, no new RPC surface to maintain.
    const missingIds = orderedIds.filter((id) => !candidatesById.has(id));
    const fetchPromise = missingIds.length > 0 ? fetchDetails(missingIds, { notificationDbName }) : Promise.resolve([]);

    let cancelled = false;
    fetchPromise
      .then((rows) => {
        if (cancelled) return;
        const fetchedById = new Map(rows.map((r) => [String(r.id), r]));

        const hydrated = orderedIds.map((idStr) => {
          const cached = candidatesById.get(idStr);
          if (cached) return cached; // parent already has full details
          const details = fetchedById.get(idStr);
          const source = sourceById.get(idStr) || { type: "previously_saved", params: {} };
          return {
            id: idStr,
            name: details?.name || "(deleted user)",
            email: details?.email || null,
            workplace: details?.workplace_name || null,
            title: details?.title_at_workplace || null,
            headline: details?.headline || null,
            bio: details?.bio || null,
            last_active_at: details?.last_active_at || null,
            profile_image_url: details?.profile_image_url || null,
            is_eligible: details?.is_eligible ?? null,
            ineligibility_reasons: details?.ineligibility_reasons || [],
            is_stub: !details,
            source,
          };
        });

        setPool(hydrated);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[useCandidatePool] hydration failed", err);
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });

    return () => {
      cancelled = true;
    };
    // notificationDbName is captured once at mount via closure — its
    // stable per picker session, and switching it mid-session would
    // be wrong anyway (the picker is for a single notification at a
    // time). fetchDetails is the only dep that can sensibly change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDetails]);

  // Add a batch of candidates from a single search. Returns { added,
  // skipped } so the caller can render "added N, M already in pool".
  // Dedup is by id; the existing source wins for already-in-pool
  // candidates.
  const addBatch = useCallback(
    (candidates, source) => {
      let added = 0;
      let skipped = 0;
      setPool((prev) => {
        const existing = new Set(prev.map((c) => c.id));
        const fresh = [];
        candidates.forEach((c) => {
          if (existing.has(c.id)) {
            skipped += 1;
          } else {
            existing.add(c.id);
            fresh.push({ ...c, source });
            added += 1;
          }
        });
        return [...prev, ...fresh];
      });
      if (added > 0) notifyUserChange();
      return { added, skipped };
    },
    [notifyUserChange],
  );

  const remove = useCallback(
    (id) => {
      setPool((prev) => prev.filter((c) => c.id !== id));
      notifyUserChange();
    },
    [notifyUserChange],
  );

  const removeMany = useCallback(
    (ids) => {
      const idSet = new Set(ids);
      setPool((prev) => prev.filter((c) => !idSet.has(c.id)));
      notifyUserChange();
    },
    [notifyUserChange],
  );

  const clear = useCallback(() => {
    setPool([]);
    notifyUserChange();
  }, [notifyUserChange]);

  // Canonical formik-shaped output. Groups candidates back into the
  // structured form selected_recipient_groups expects; the model
  // validator enforces ids == flatten(groups.user_ids).uniq.
  // Also returns `candidates` (the full pool with display details) so
  // the parent form can populate the inline RecipientSummary without
  // re-fetching from PostgREST.
  const toFormData = useCallback(() => {
    const ids = pool.map((c) => c.id);
    const groupsByKey = new Map();
    pool.forEach((c) => {
      const key = JSON.stringify(c.source);
      if (!groupsByKey.has(key)) {
        groupsByKey.set(key, {
          source_type: c.source.type,
          source_params: c.source.params,
          user_ids: [],
        });
      }
      groupsByKey.get(key).user_ids.push(c.id);
    });
    return { ids, groups: Array.from(groupsByKey.values()), candidates: pool };
  }, [pool]);

  return {
    pool,
    hydrating,
    addBatch,
    remove,
    removeMany,
    clear,
    toFormData,
    isDirty: pool.length > 0,
  };
}
